import * as React from 'react';
import { styled } from '@glitz/react';
import Image from 'webapp/components/image';
import { createMaxMobileViewportDecorator, createMinLargeViewportDecorator } from 'webapp/viewport';
import { useMatchMobile } from '@avensia/excite';
import { addSelectContentEventToDataLayer } from 'webapp/tracking/events/content';
import { isSvenssons } from 'webapp/theme';
import Link from 'webapp/components/Link';

export const ArticleCard = React.memo(
  (props: NordicNest.Api.ArticleListItemApiModel & { backgroundColor?: string; useImageWrapper?: boolean }) => {
    const backgroundDecorator = styled(
      {
        backgroundColor: props.backgroundColor ? props.backgroundColor : isSvenssons ? 'none' : t => t.monochrome.color,
      },
      isSvenssons && {
        paddingBottom: t => (props.backgroundColor ? t.sharedSpacing.m : 0),
      },
    );
    const isMobile = useMatchMobile();

    const image = props.useImageWrapper ? (
      <ImageWrapper>
        <WideArticleImage image={isMobile ? props.image : props.wideImage} preset={'tiny'} />
      </ImageWrapper>
    ) : (
      <ArticleImage image={props.image} preset={'tiny'} />
    );

    const textContent = isSvenssons ? (
      <>
        <Category>{props.category}</Category>
        <Title>{props.title}</Title>
        <Date>{props.date}</Date>
      </>
    ) : (
      <>
        <Date>{props.date}</Date>
        <Title>{props.title}</Title>
      </>
    );

    return (
      <Base>
        <BaseLink
          to={props.url}
          onClick={() => addSelectContentEventToDataLayer({ id: props.id, type: 'article', link: props.url })}
        >
          {image}
          <Text css={backgroundDecorator}>{textContent}</Text>
        </BaseLink>
        {!isSvenssons && <Sticker to={props.categoryUrl}>{props.category}</Sticker>}
      </Base>
    );
  },
);

const Base = styled.div({
  position: 'relative',
  height: '100%',
});

const BaseLink = styled(Link, {
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const ImageWrapper = styled.div(
  createMaxMobileViewportDecorator({
    paddingTop: '76.991%',
  }),
  {
    paddingTop: '50%',
    position: 'relative',
  },
);

const ArticleImage = styled(Image, {
  width: '100%',
});

const WideArticleImage = styled(ArticleImage, {
  position: 'absolute',
  height: '100%',
  objectFit: 'cover',
  top: 0,
});

const Text = styled.div(
  {
    padding: { x: t => t.sharedSpacing.m, y: t => (isSvenssons ? t.sharedSpacing.m : t.spacing.l) },
    flexGrow: 1,
  },
  isSvenssons &&
    styled(
      createMinLargeViewportDecorator({
        padding: { x: t => t.spacing.m, top: t => t.spacing.s },
      }),
      {
        textAlign: 'center',
      },
    ),
);

const Category = styled.div({
  fontFamily: 'charter',
  textTransform: 'uppercase',
  fontSize: t => t.fontSize.xxs,
  marginBottom: t => t.spacing.xxs,
});

const Date = styled.div(
  {
    fontSize: t => t.sharedFontSize.xxs,
  },
  !isSvenssons && {
    textTransform: 'uppercase',
    color: t => t.dark.color,
    marginBottom: t => t.sharedSpacing.s,
  },
  isSvenssons &&
    createMinLargeViewportDecorator({
      fontSize: t => t.sharedFontSize.xs,
    }),
);

const Title = styled.h5(
  {
    fontSize: t => t.fontSize.s,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  isSvenssons &&
    createMinLargeViewportDecorator({
      fontSize: t => t.fontSize.l,
    }),
);

/** @glitz-dynamic */
const Sticker = styled(Link, {
  padding: { x: t => t.spacing.m, y: t => t.spacing.xxs },
  fontSize: t => t.fontSize.xs,
  backgroundColor: t => t.darkGrey,
  color: 'white',
  ':visited': {
    color: 'white',
  },
  ':hover': {
    color: 'white',
  },
  position: 'absolute',
  top: t => t.spacing.m,
  right: 0,
});
